import { useState, useEffect, useRef } from 'react';
import { Search, Plane, Train, Car, Shuffle, RefreshCw, MapPin, Calendar, Minus, Plus, Navigation, Users, Star, Settings } from 'lucide-react';
import { TRAVEL_PACE_OPTIONS, BUDGET_OPTIONS, TRAVELER_PROFILES, WHO_IS_COMING_OPTIONS } from '../../constants/travelerProfiles';
import { IconFromName } from '../../utils/iconResolver';

function SectionCard({ icon: Icon, label, children }) {
  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm">
      <div className="flex items-center gap-2.5 mb-4">
        <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-coral-50 flex-shrink-0">
          <Icon size={14} className="text-coral-500" />
        </div>
        <span className="text-xs font-semibold tracking-wider uppercase text-charcoal-400">{label}</span>
      </div>
      {children}
    </div>
  );
}

const TRAVEL_MODE_OPTIONS = [
  { value: 'flight', label: 'Flight', icon: Plane },
  { value: 'train', label: 'Train', icon: Train },
  { value: 'car', label: 'Car', icon: Car },
  { value: 'van', label: 'Van', icon: Car },
  { value: 'mixed', label: 'Mixed', icon: Shuffle },
];

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

function useMapboxSearch(query, debounceRef) {
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => {
    if (query.length < 2 || !MAPBOX_TOKEN) {
      setSuggestions([]);
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${MAPBOX_TOKEN}&types=place,country,region,locality&limit=5&language=en&autocomplete=true`
        );
        const data = await res.json();
        setSuggestions(
          (data.features || []).map(f => ({
            name: f.place_type[0] === 'country' ? f.text : f.place_name,
          }))
        );
      } catch {
        setSuggestions([]);
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [query, debounceRef]);

  return [suggestions, setSuggestions];
}

export default function TripParametersPanel({ itinerary, onParametersChanged, isRegenerating, onStartBlank }) {
  const [destination, setDestination] = useState(itinerary.destination || '');
  const [tripOrigin, setTripOrigin] = useState(itinerary.trip_origin || '');
  const [travelMode, setTravelMode] = useState(itinerary.travel_mode || '');
  const [travelPace, setTravelPace] = useState(itinerary.travel_pace || 'balanced');
  const [budget, setBudget] = useState(itinerary.budget || 'medium');
  const [travelerProfiles, setTravelerProfiles] = useState(itinerary.traveler_profiles || []);
  const [whoIsComing, setWhoIsComing] = useState(itinerary.who_is_coming || []);
  const [tripLength, setTripLength] = useState(itinerary.trip_length || 7);
  const [startDate, setStartDate] = useState(itinerary.start_date ? itinerary.start_date.split('T')[0] : '');
  const [mustDoActivities, setMustDoActivities] = useState(itinerary.must_do_activities || '');

  const [showDestSuggestions, setShowDestSuggestions] = useState(false);
  const [showOriginSuggestions, setShowOriginSuggestions] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const destDebounceRef = useRef(null);
  const originDebounceRef = useRef(null);

  const [destSuggestions, setDestSuggestions] = useMapboxSearch(destination, destDebounceRef);
  const [originSuggestions, setOriginSuggestions] = useMapboxSearch(tripOrigin, originDebounceRef);

  useEffect(() => {
    const currentStart = itinerary.start_date ? itinerary.start_date.split('T')[0] : '';
    const changed =
      destination !== (itinerary.destination || '') ||
      tripOrigin !== (itinerary.trip_origin || '') ||
      travelMode !== (itinerary.travel_mode || '') ||
      travelPace !== (itinerary.travel_pace || 'balanced') ||
      budget !== (itinerary.budget || 'medium') ||
      tripLength !== (itinerary.trip_length || 7) ||
      startDate !== currentStart ||
      JSON.stringify(travelerProfiles) !== JSON.stringify(itinerary.traveler_profiles || []) ||
      JSON.stringify(whoIsComing) !== JSON.stringify(itinerary.who_is_coming || []) ||
      mustDoActivities !== (itinerary.must_do_activities || '');
    setHasChanges(changed);
  }, [destination, tripOrigin, travelMode, travelPace, budget, travelerProfiles, whoIsComing, tripLength, startDate, mustDoActivities, itinerary]);

  useEffect(() => {
    setDestination(itinerary.destination || '');
    setTripOrigin(itinerary.trip_origin || '');
    setTravelMode(itinerary.travel_mode || '');
    setTravelPace(itinerary.travel_pace || 'balanced');
    setBudget(itinerary.budget || 'medium');
    setTravelerProfiles(itinerary.traveler_profiles || []);
    setWhoIsComing(itinerary.who_is_coming || []);
    setTripLength(itinerary.trip_length || 7);
    setStartDate(itinerary.start_date ? itinerary.start_date.split('T')[0] : '');
    setMustDoActivities(itinerary.must_do_activities || '');
  }, [itinerary.id]);

  const computeEndDate = (start, length) => {
    if (!start) return null;
    const d = new Date(start);
    d.setDate(d.getDate() + length - 1);
    return d.toISOString().split('T')[0];
  };

  const handleRegenerate = () => {
    const dest = destination || itinerary.destination;
    const endDate = computeEndDate(startDate, tripLength);
    onParametersChanged({
      destination: dest,
      title: `${dest} - ${tripLength} days`,
      trip_origin: tripOrigin || null,
      travel_mode: travelMode || null,
      travel_pace: travelPace,
      budget,
      traveler_profiles: travelerProfiles,
      who_is_coming: whoIsComing,
      trip_length: tripLength,
      must_do_activities: mustDoActivities || null,
      ...(startDate ? { start_date: startDate, end_date: endDate } : {}),
    });
  };

  const toggleProfile = (profileId) => {
    setTravelerProfiles(prev =>
      prev.includes(profileId)
        ? prev.filter(p => p !== profileId)
        : [...prev, profileId]
    );
  };

  // 'solo' and 'undecided' are exclusive — picking either clears any other
  // selection, and picking anything else clears those two.
  const toggleWhoIsComing = (value) => {
    setWhoIsComing(prev => {
      const isExclusive = value === 'solo' || value === 'undecided';
      if (prev.includes(value)) return prev.filter(v => v !== value);
      if (isExclusive) return [value];
      return [...prev.filter(v => v !== 'solo' && v !== 'undecided'), value];
    });
  };

  const inputClass = "w-full pl-9 pr-3 py-3 text-sm border border-platinum-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-coral-300 bg-platinum-50 placeholder:text-platinum-400 text-charcoal-500";

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-platinum-100 bg-white flex-shrink-0">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-xl bg-charcoal-500 flex items-center justify-center flex-shrink-0">
            <Settings size={15} className="text-white" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-charcoal-500 leading-tight">Trip Settings</h3>
            <p className="text-xs text-platinum-500 mt-0.5">
              {destination || 'No destination'} · {tripLength} {tripLength === 1 ? 'day' : 'days'}
            </p>
          </div>
        </div>
      </div>

      {/* Scrollable sections */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-platinum-50">

        {/* WHERE */}
        <SectionCard icon={MapPin} label="Destination">
          <div className="space-y-2">
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-coral-400" size={14} />
              <input
                type="text"
                value={destination}
                onChange={(e) => { setDestination(e.target.value); setShowDestSuggestions(true); }}
                onFocus={() => setShowDestSuggestions(true)}
                onBlur={() => setTimeout(() => setShowDestSuggestions(false), 200)}
                placeholder="Where are you going?"
                className={inputClass}
              />
              {showDestSuggestions && destSuggestions.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl shadow-lg border border-platinum-200 z-20 max-h-40 overflow-y-auto">
                  {destSuggestions.map((s, i) => (
                    <button
                      key={i}
                      onMouseDown={() => { setDestination(s.name); setShowDestSuggestions(false); }}
                      className="w-full px-3 py-2.5 text-sm text-left hover:bg-coral-50 hover:text-coral-700 transition-colors first:rounded-t-xl last:rounded-b-xl"
                    >
                      {s.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-platinum-400" size={14} />
              <input
                type="text"
                value={tripOrigin}
                onChange={(e) => { setTripOrigin(e.target.value); setShowOriginSuggestions(true); }}
                onFocus={() => setShowOriginSuggestions(true)}
                onBlur={() => setTimeout(() => setShowOriginSuggestions(false), 200)}
                placeholder="Departing from..."
                className={inputClass}
              />
              {showOriginSuggestions && originSuggestions.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl shadow-lg border border-platinum-200 z-20 max-h-40 overflow-y-auto">
                  {originSuggestions.map((s, i) => (
                    <button
                      key={i}
                      onMouseDown={() => { setTripOrigin(s.name); setShowOriginSuggestions(false); }}
                      className="w-full px-3 py-2.5 text-sm text-left hover:bg-coral-50 hover:text-coral-700 transition-colors first:rounded-t-xl last:rounded-b-xl"
                    >
                      {s.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </SectionCard>

        {/* WHEN */}
        <SectionCard icon={Calendar} label="Dates">
          <div className="flex gap-2 items-center">
            <div className="relative flex-1">
              <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 text-platinum-400" size={13} />
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full pl-8 pr-2 py-2.5 text-sm border border-platinum-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-coral-300 bg-platinum-50 text-charcoal-500"
              />
            </div>
            <div className="flex items-center gap-1 bg-platinum-50 rounded-xl border border-platinum-200 px-1">
              <button
                type="button"
                onClick={() => setTripLength(prev => Math.max(1, prev - 1))}
                className="p-2 text-platinum-500 hover:text-coral-500 transition-colors"
              >
                <Minus size={13} />
              </button>
              <span className="text-sm font-bold text-charcoal-500 w-12 text-center">
                {tripLength}d
              </span>
              <button
                type="button"
                onClick={() => setTripLength(prev => Math.min(365, prev + 1))}
                className="p-2 text-platinum-500 hover:text-coral-500 transition-colors"
              >
                <Plus size={13} />
              </button>
            </div>
          </div>
        </SectionCard>

        {/* HOW */}
        <SectionCard icon={Navigation} label="Travel Style">
          <div className="space-y-3">
            {/* Travel mode */}
            <div className="flex flex-wrap gap-2">
              {TRAVEL_MODE_OPTIONS.map(opt => {
                const Icon = opt.icon;
                const active = travelMode === opt.value;
                return (
                  <button
                    key={opt.value}
                    onClick={() => setTravelMode(active ? '' : opt.value)}
                    className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-medium transition-all ${
                      active
                        ? 'bg-coral-400 text-white shadow-sm'
                        : 'bg-white border border-platinum-200 text-charcoal-400 hover:border-coral-300 hover:text-coral-500'
                    }`}
                  >
                    <Icon size={13} />
                    {opt.label}
                  </button>
                );
              })}
            </div>

            {/* Pace */}
            <div className="flex flex-wrap gap-2">
              {TRAVEL_PACE_OPTIONS.map(opt => {
                const active = travelPace === opt.value;
                return (
                  <button
                    key={opt.value}
                    onClick={() => setTravelPace(opt.value)}
                    title={opt.description}
                    className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-medium transition-all ${
                      active
                        ? 'bg-coral-400 text-white shadow-sm'
                        : 'bg-white border border-platinum-200 text-charcoal-400 hover:border-coral-300 hover:text-coral-500'
                    }`}
                  >
                    <IconFromName name={opt.iconName} size={13} style={{ color: active ? '#fff' : undefined }} />
                    {opt.label}
                  </button>
                );
              })}
            </div>

            {/* Budget */}
            <div className="flex flex-wrap gap-2">
              {BUDGET_OPTIONS.map(opt => {
                const active = budget === opt.value;
                return (
                  <button
                    key={opt.value}
                    onClick={() => setBudget(opt.value)}
                    title={opt.description}
                    className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-medium transition-all ${
                      active
                        ? 'bg-coral-400 text-white shadow-sm'
                        : 'bg-white border border-platinum-200 text-charcoal-400 hover:border-coral-300 hover:text-coral-500'
                    }`}
                  >
                    <IconFromName name={opt.iconName} size={13} style={{ color: active ? '#fff' : undefined }} />
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
        </SectionCard>

        {/* WHO */}
        <SectionCard icon={Users} label="Traveler Type">
          <div className="flex flex-wrap gap-2">
            {TRAVELER_PROFILES.map(p => {
              const active = travelerProfiles.includes(p.id);
              return (
                <button
                  key={p.id}
                  onClick={() => toggleProfile(p.id)}
                  title={p.description}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold transition-all ${
                    active
                      ? 'bg-coral-400 text-white shadow-sm'
                      : 'bg-platinum-50 border border-platinum-200 text-charcoal-400 hover:border-coral-300 hover:text-coral-500'
                  }`}
                >
                  <IconFromName name={p.iconName} size={12} style={{ color: active ? '#fff' : p.color }} />
                  {p.title.replace('The ', '')}
                </button>
              );
            })}
          </div>
        </SectionCard>

        {/* WHO'S COMING */}
        <SectionCard icon={Users} label="Who's Coming">
          <div className="flex flex-wrap gap-2">
            {WHO_IS_COMING_OPTIONS.map(option => {
              const active = whoIsComing.includes(option.value);
              return (
                <button
                  key={option.value}
                  onClick={() => toggleWhoIsComing(option.value)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold transition-all ${
                    active
                      ? 'bg-coral-400 text-white shadow-sm'
                      : 'bg-platinum-50 border border-platinum-200 text-charcoal-400 hover:border-coral-300 hover:text-coral-500'
                  }`}
                >
                  <IconFromName name={option.iconName} size={12} style={{ color: active ? '#fff' : undefined }} />
                  {option.label}
                </button>
              );
            })}
          </div>
        </SectionCard>

        {/* WHAT */}
        <SectionCard icon={Star} label="Must-dos">
          <textarea
            value={mustDoActivities}
            onChange={(e) => setMustDoActivities(e.target.value)}
            placeholder="e.g. Trolltunga hike, northern lights, a specific restaurant..."
            rows={3}
            className="w-full px-3 py-3 text-sm border border-platinum-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-coral-300 bg-platinum-50 resize-none placeholder:text-platinum-400 text-charcoal-500 leading-relaxed"
          />
        </SectionCard>

      </div>

      {/* Regenerate CTA */}
      <div className="p-3 border-t border-platinum-100 bg-white flex-shrink-0">
        <button
          onClick={handleRegenerate}
          disabled={!hasChanges || isRegenerating}
          className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold transition-all ${
            hasChanges && !isRegenerating
              ? 'bg-coral-400 hover:bg-coral-500 text-white shadow-sm'
              : 'bg-platinum-100 text-platinum-400 cursor-not-allowed'
          }`}
        >
          <RefreshCw size={14} className={isRegenerating ? 'animate-spin' : ''} />
          {isRegenerating ? 'Regenerating...' : 'Regenerate Itinerary'}
        </button>
        {hasChanges && !isRegenerating && (
          <p className="text-[10px] text-center text-platinum-400 mt-1.5">
            Settings changed — regenerate to apply
          </p>
        )}
        {onStartBlank && (
          <button
            onClick={onStartBlank}
            className="w-full mt-1.5 text-[11px] text-platinum-400 hover:text-charcoal-500 transition-colors text-center underline underline-offset-2"
          >
            Or start with a blank itinerary
          </button>
        )}
      </div>
    </div>
  );
}
